import { useEffect, useState } from "react";
import type { OrdreIntervention } from "types/ordre";
import { isAPrevoir } from "types/ordre";
import { ordresApi } from "@services/api/ordres";

// Interventions (with hours / "à prévoir") live on the ordre, not on the
// lightweight job/appointment record, so hover panels fetch them lazily by
// ordre_id and cache the result to keep repeat hovers instant. Shared by the
// planning reception tooltip (AppointmentTooltip) and the atelier grid job
// tooltip so both render the "Interventions (N)" block identically.
const interventionsCache = new Map<string, OrdreIntervention[]>();

/**
 * The dark-tooltip "Interventions (N)" block: a vertical list of intervention
 * labels with their estimated hours, à-prévoir styling, and a "+N autre(s)"
 * overflow. Falls back to plain string labels (no hours) when the ordre can't be
 * loaded — e.g. an atelier job that only carries labels.
 */
export default function OrdreInterventionsHint({
  ordreId,
  fallbackLabels,
}: {
  ordreId?: string;
  fallbackLabels?: string[];
}) {
  const [items, setItems] = useState<OrdreIntervention[] | null>(
    () => (ordreId ? interventionsCache.get(ordreId) ?? null : null),
  );

  useEffect(() => {
    if (!ordreId || interventionsCache.has(ordreId)) return;
    let alive = true;
    ordresApi
      .get(ordreId)
      .then((ordre) => {
        const list = ordre.interventions || [];
        interventionsCache.set(ordreId, list);
        if (alive) setItems(list);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [ordreId]);

  const labels = fallbackLabels ?? [];
  // Nothing to show and nothing to load → render nothing.
  if (!ordreId && labels.length === 0) return null;
  // ordre known but not fetched yet, and no fallback → wait silently.
  if (items === null && labels.length === 0) return null;

  const count = items ? items.length : labels.length;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-700" style={{ whiteSpace: "normal" }}>
      <div className="text-h5 text-neutral-400 font-medium mb-1">Interventions ({count})</div>
      {count === 0 ? (
        <div className="text-h5 text-neutral-500 italic">Aucune intervention</div>
      ) : items ? (
        <div className="flex flex-col gap-0.5">
          {items.slice(0, 8).map((it) => {
            const aPrevoir = isAPrevoir(it);
            return (
              <div key={it.id} className="flex flex-row items-baseline justify-between gap-2">
                <span
                  className={`text-h5 truncate ${aPrevoir ? "text-amber-300/80 line-through decoration-amber-400/50" : "text-white"}`}
                >
                  {it.label}
                  {aPrevoir ? " · à prévoir" : ""}
                </span>
                {it.estimated_hours ? (
                  <span className="text-h5 text-neutral-400 flex-shrink-0">{it.estimated_hours}h</span>
                ) : null}
              </div>
            );
          })}
          {items.length > 8 && (
            <div className="text-h5 text-neutral-500">+{items.length - 8} autre(s)</div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-0.5">
          {labels.slice(0, 8).map((l, i) => (
            <div key={i} className="text-h5 text-white truncate">
              {l}
            </div>
          ))}
          {labels.length > 8 && (
            <div className="text-h5 text-neutral-500">+{labels.length - 8} autre(s)</div>
          )}
        </div>
      )}
    </div>
  );
}
